import React, { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Trash2, Loader2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

/**
 * One-click "Empty Trash" — super admin only.
 *
 * Drops into every Trash tab (Students, Instructors, Users, Programs).
 * Renders nothing for non-super-admins or when the trash is already
 * empty, so it never clutters lower-privilege views.
 *
 * Guards against accidental clicks with a typed-count confirmation
 * gate. The backend also enforces ``confirm_count == server count``
 * so concurrent edits can't blow away more than the user saw.
 */
export default function EmptyTrashButton({
    resource,
    count,
    label,
    onEmptied,
    testId = "empty-trash-btn",
}) {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [typed, setTyped] = useState("");
    const [submitting, setSubmitting] = useState(false);

    if (user?.role !== "super_admin" || !count || count <= 0) {
        return null;
    }

    const expected = String(count);
    const matches = typed.trim() === expected;

    const reset = () => {
        setTyped("");
        setSubmitting(false);
    };

    const runEmpty = async () => {
        if (!matches) return;
        setSubmitting(true);
        try {
            const { data } = await api.post("/admin/trash/empty", {
                resource,
                confirm_count: count,
            });
            const n = data?.purged ?? 0;
            toast.success(
                n === 0
                    ? "Trash was already empty."
                    : `Permanently deleted ${n} ${label || resource}.`,
            );
            setOpen(false);
            reset();
            onEmptied?.(data);
        } catch (e) {
            const msg = e?.response?.data?.detail || "Empty trash failed. Try again.";
            toast.error(typeof msg === "string" ? msg : "Empty trash failed.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <AlertDialog
            open={open}
            onOpenChange={(o) => {
                setOpen(o);
                if (!o) reset();
            }}
        >
            <Button
                variant="outline"
                size="sm"
                onClick={() => setOpen(true)}
                className="border-destructive/40 text-destructive hover:bg-destructive hover:text-destructive-foreground"
                data-testid={testId}
            >
                <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                Empty Trash ({count})
            </Button>
            <AlertDialogContent data-testid={`${testId}-dialog`}>
                <AlertDialogHeader>
                    <AlertDialogTitle className="flex items-center gap-2 font-display">
                        <ShieldAlert className="h-5 w-5 text-destructive" />
                        Empty trash permanently?
                    </AlertDialogTitle>
                    <AlertDialogDescription asChild>
                        <div className="space-y-3 text-sm text-muted-foreground">
                            <p>
                                You are about to permanently delete{" "}
                                <span className="font-semibold text-foreground">
                                    {count} {label || resource}
                                </span>{" "}
                                and every record they touched — enrollments, progress,
                                quiz attempts, certificates, and discussion posts (where
                                applicable). This cannot be undone.
                            </p>
                            <p>
                                Super admin and your own account are never affected.
                                Type the number{" "}
                                <span className="font-mono font-semibold text-foreground">
                                    {expected}
                                </span>{" "}
                                below to confirm.
                            </p>
                            <div>
                                <Label htmlFor="empty-trash-confirm" className="text-xs">
                                    Type {expected} to confirm
                                </Label>
                                <Input
                                    id="empty-trash-confirm"
                                    value={typed}
                                    onChange={(e) => setTyped(e.target.value)}
                                    placeholder={expected}
                                    autoFocus
                                    disabled={submitting}
                                    data-testid={`${testId}-confirm-input`}
                                    className="mt-1 font-mono text-foreground"
                                />
                            </div>
                        </div>
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel
                        disabled={submitting}
                        data-testid={`${testId}-cancel`}
                    >
                        Cancel
                    </AlertDialogCancel>
                    <AlertDialogAction
                        onClick={runEmpty}
                        disabled={!matches || submitting}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
                        data-testid={`${testId}-confirm`}
                    >
                        {submitting && (
                            <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                        )}
                        <Trash2 className="h-4 w-4 mr-1.5" />
                        Delete everything
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}
